import type { ApiError } from '@blesk/contracts';

/**
 * Общий клиент API.
 * Cookie сессии отправляется браузером (`credentials: 'include'`) — токенов
 * и ключей в коде SPA нет и быть не может (§18).
 */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiError,
  ) {
    super(payload.message);
    this.name = 'ApiRequestError';
  }
}

/** Сессия истекла или отозвана: SPA обязана вернуть человека на вход (§18). */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}

/** Прав не хватает. Сервер решил это сам — интерфейс только показывает результат (§3). */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 403;
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

/** Сборка адреса с фильтрами: пустые значения не уезжают в строку запроса. */
export function withQuery(path: string, params: QueryParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({
      code: 'NETWORK_ERROR',
      message: 'Нет связи с сервисом',
    }))) as ApiError;
    throw new ApiRequestError(res.status, payload);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiGet = <T>(path: string, params?: QueryParams) =>
  request<T>(withQuery(path, params));

export const apiPost = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const apiPatch = <T>(path: string, body?: unknown) =>
  request<T>(path, {
    method: 'PATCH',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

export const apiPut = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) });
